import { uuid } from '~/utils';

function murmurX64Hash128(key: string, seed = 0) {
  // 常量定义（64 位常量拆分为高低 32 位）
  const c1 = [0x87c37b91, 0x114253d5]; // 0x87c37b91114253d5
  const c2 = [0x4cf5ad43, 0x2745937f]; // 0x4cf5ad432745937f
  const m = 5;
  const n = [0x52dce729, 0]; // 0x52dce729

  // 将输入转换为 Uint8Array
  const encoder = new TextEncoder();
  const data = typeof key === 'string' ? encoder.encode(key) : new Uint8Array(key);
  const length = data.length;

  // 初始化 h1 和 h2（拆分为高低 32 位）
  let h1 = [seed >>> 0, 0];
  let h2 = [seed >>> 0, 0];

  // 辅助函数：64 位乘法（模拟）
  function mul64(a: number[], b: number[]) {
    const aLo = a[1] >>> 0,
      aHi = a[0] >>> 0;
    const bLo = b[1] >>> 0,
      bHi = b[0] >>> 0;
    const lo = aLo * bLo;
    const hi = (aHi * bLo + aLo * bHi + (lo >>> 0) / 0x100000000) | 0;
    return [hi >>> 0, lo >>> 0];
  }

  // 辅助函数：64 位左旋转
  function rotl64(x: number[], r: number) {
    const hi = x[0] >>> 0,
      lo = x[1] >>> 0;
    if (r >= 32) {
      return [lo >>> (64 - r), hi >>> (64 - r)];
    }
    const carry = (lo >>> (32 - r)) | (hi << r);
    return [(hi << r) | (lo >>> (32 - r)), (lo << r) | carry];
  }

  // 辅助函数：64 位异或
  function xor64(a: number[], b: number[]) {
    return [a[0] ^ b[0], a[1] ^ b[1]];
  }

  // 辅助函数：64 位加法
  function add64(a: number[], b: number[]) {
    const lo = (a[1] >>> 0) + (b[1] >>> 0);
    const carry = (lo / 0x100000000) | 0;
    const hi = (a[0] >>> 0) + (b[0] >>> 0) + carry;
    return [hi >>> 0, lo >>> 0];
  }

  // 处理 16 字节块
  let i = 0;
  for (; i <= length - 16; i += 16) {
    const part1 = (data[i] | (data[i + 1] << 8) | (data[i + 2] << 16) | (data[i + 3] << 24)) >>> 0;

    const part2 =
      (data[i + 4] | (data[i + 5] << 8) | (data[i + 6] << 16) | (data[i + 7] << 24)) >>> 0;

    const part3 =
      (data[i + 8] | (data[i + 9] << 8) | (data[i + 10] << 16) | (data[i + 11] << 24)) >>> 0;

    const part4 =
      (data[i + 12] | (data[i + 13] << 8) | (data[i + 14] << 16) | (data[i + 15] << 24)) >>> 0;

    const k1 = [part1, part2];
    const k2 = [part3, part4];

    // k1 处理
    let t = mul64(k1, c1);
    t = rotl64(t, 31);
    t = mul64(t, c2);
    h1 = xor64(h1, t);
    h1 = rotl64(h1, 27);
    h1 = add64(mul64(h1, [0, m]), n);

    // k2 处理
    t = mul64(k2, c2);
    t = rotl64(t, 33);
    t = mul64(t, c1);
    h2 = xor64(h2, t);
    h2 = rotl64(h2, 31);
    h2 = add64(mul64(h2, [0, m]), [0x38495ab5, 0]);
  }

  // 处理尾部
  let k1 = [0, 0],
    k2 = [0, 0];
  switch (length - i) {
    case 15:
      k2[1] ^= data[i + 14] << 16;
      break;
    case 14:
      k2[1] ^= data[i + 13] << 8;
      break;
    case 13:
      k2[1] ^= data[i + 12];
      break;
    case 12:
      k2[0] ^= data[i + 11] << 24;
      break;
    case 11:
      k2[0] ^= data[i + 10] << 16;
      break;
    case 10:
      k2[0] ^= data[i + 9] << 8;
      break;
    case 9:
      k2[0] ^= data[i + 8];
      break;
    case 8:
      k1[1] ^= data[i + 7] << 24;
      break;
    case 7:
      k1[1] ^= data[i + 6] << 16;
      break;
    case 6:
      k1[1] ^= data[i + 5] << 8;
      break;
    case 5:
      k1[1] ^= data[i + 4];
      break;
    case 4:
      k1[0] ^= data[i + 3] << 24;
      break;
    case 3:
      k1[0] ^= data[i + 2] << 16;
      break;
    case 2:
      k1[0] ^= data[i + 1] << 8;
      break;
    case 1:
      k1[0] ^= data[i];
  }

  // 尾部混合
  k1 = mul64(mul64(k1, c1), c2);
  h1 = xor64(h1, k1);
  k2 = mul64(mul64(k2, c2), c1);
  h2 = xor64(h2, k2);

  // 最终化
  h1 = xor64(h1, [0, length]);
  h2 = xor64(h2, [0, length]);
  h1 = add64(h1, h2);
  h2 = add64(h2, h1);

  for (let j = 0; j < 2; j++) {
    h1 = mul64(xor64(h1, [h1[0] >>> 1, h1[1] >>> 1]), [0xff51afd7, 0xed558ccd]);
    h2 = mul64(xor64(h2, [h2[0] >>> 1, h2[1] >>> 1]), [0xc4ceb9fe, 0x1a85ec53]);
    h1 = xor64(h1, [h1[0] >>> 1, h1[1] >>> 1]);
    h2 = xor64(h2, [h2[0] >>> 1, h2[1] >>> 1]);
  }

  h1 = add64(h1, h2);
  h2 = add64(h2, h1);

  // 返回 128 位哈希值（16 字节十六进制字符串）
  return (
    (h1[0] >>> 0).toString(16).padStart(8, '0') +
    (h1[1] >>> 0).toString(16).padStart(8, '0') +
    (h2[0] >>> 0).toString(16).padStart(8, '0') +
    (h2[1] >>> 0).toString(16).padStart(8, '0')
  );
}

// 获取 Canvas 指纹
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.textBaseline = 'top';
    ctx.font = '16px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('fingerprint!', 2, 15);
    return canvas.toDataURL();
  } catch (err) {
    console.error(err);
    return 'unknown';
  }
}

// 获取 WebGL Renderer
function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    const debugInfo = gl!.getExtension('WEBGL_debug_renderer_info')!;
    return gl?.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
  } catch (err) {
    console.error(err);
    return 'unknown';
  }
}

// 获取 audio 指纹
async function getAudioFingerprint() {
  return new Promise(resolve => {
    const ctx = new (window.OfflineAudioContext || (window as any).webkitOfflineAudioContext)(
      1,
      44100,
      44100
    );
    const oscillator = ctx.createOscillator();
    const analyser = ctx.createAnalyser();
    const gain = ctx.createGain();
    const scriptProcessor = ctx.createScriptProcessor(4096, 1, 1);

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(10000, ctx.currentTime);
    gain.gain.setValueAtTime(0, ctx.currentTime); // Mute output

    oscillator.connect(gain);
    gain.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(ctx.destination);

    const fingerprintData: number[] = [];

    scriptProcessor.onaudioprocess = event => {
      const data = new Float32Array(analyser.frequencyBinCount);
      analyser.getFloatFrequencyData(data);
      fingerprintData.push(...data.slice(0, 10));
    };

    oscillator.start(0);
    ctx.startRendering().then(() => {
      oscillator.stop();
      resolve(fingerprintData.slice(0, 10).join(','));
    });
  });
}

function getFontFingerprint() {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testFonts = [
    'Arial',
    'Verdana',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Comic Sans MS',
    'Trebuchet MS',
    'Impact',
  ];

  const testString = 'fingerprint';
  const testSize = '72px';
  const span = document.createElement('span');
  span.style.position = 'absolute';
  span.style.left = '-9999px';
  span.style.fontSize = testSize;
  span.textContent = testString;
  document.body.appendChild(span);

  const defaultWidths: Record<string, number> = {};
  baseFonts.forEach(function (base) {
    span.style.fontFamily = base;
    defaultWidths[base] = span.offsetWidth;
  });

  const detected: string[] = [];
  testFonts.forEach(function (font) {
    span.style.fontFamily = `${font}, monospace`;
    if (span.offsetWidth !== defaultWidths['monospace']) {
      detected.push(font);
    }
  });

  document.body.removeChild(span);
  return detected.join(',');
}

// 收集指纹组件
async function collectFingerprintComponents() {
  const canvasFingerprint = getCanvasFingerprint();
  const webglFingerprint = getWebGLFingerprint();
  const audioFingerprint = await getAudioFingerprint();
  const fontFingerprint = getFontFingerprint();
  return [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    canvasFingerprint,
    webglFingerprint,
    audioFingerprint,
    fontFingerprint,
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
  ];
}

// 用原生 crypto.subtle.digest 生成 SHA-256 哈希
async function generateFingerprint() {
  const components = await collectFingerprintComponents();
  return murmurX64Hash128(components.join('###'), 12345) + '-type2';
}

export const getFingerprint = async () => {
  try {
    const fp = await import('@fingerprintjs/fingerprintjs').then(m => m.default);
    const { visitorId } = await fp.load().then(fp => fp.get());
    if (!visitorId) {
      return generateFingerprint();
    }
    return visitorId + '-type1';
  } catch (err) {
    try {
      return generateFingerprint();
    } catch (err) {
      return uuid() + '-type3';
    }
  }
};
